import { StarIcon } from "@phosphor-icons/react";
import { useIntl } from "react-intl";

import { Image } from "@/components/Image/Image";
import { BoardBasisName } from "@/features/hotels/components/hotel/BoardBasisName";
import { HotelRoom } from "@/features/hotels/components/hotel/HotelRoom";
import { boardIcons } from "@/features/hotels/components/hotels/BoardIcons";
import { HotelName } from "@/features/hotels/components/hotels/HotelName";
import type { HotelBooking, HotelData } from "@/features/hotels/types/types";

type Props = {
  hotel: HotelData;
  hotelBookingData: HotelBooking;
  rooms?: { adults: number; children: number }[];
};

export const HotelAndRoomCard = ({ hotel, hotelBookingData, rooms }: Props) => {
  const intl = useIntl();

  return (
    <div className="section-card flex flex-col gap-4">
      <div className="flex gap-4">
        <div>
          {hotel.mainImageUrl && (
            <div className="flex-end flex w-full">
              <Image
                className="min-h-32 min-w-24 rounded-2xl object-cover"
                width="90"
                height="130"
                src={hotel.mainImageUrl}
                alt={hotel.name}
              />
            </div>
          )}
        </div>
        <div>
          <span className="mb-1 font-bold text-text-title">
            <HotelName name={hotel.name} />
          </span>
          <div className="flex flex-col gap-2">
            {!!hotel.stars && (
              <div className="flex items-center gap-2">
                <div className="flex">
                  {Array.from(Array(Number(hotel.stars))).map((_, index) => (
                    <StarIcon
                      key={index}
                      size={12}
                      color="var(--color-semantic-error-pressed-medium)"
                      weight="fill"
                    />
                  ))}
                </div>
                <span className="text-sm text-text-secondary">
                  {intl.formatMessage(
                    {
                      id: "hotels.card.stars",
                      defaultMessage: "{stars}-star hotel",
                    },
                    { stars: hotel.stars },
                  )}
                </span>
              </div>
            )}
            {hotel?.boardBasis?.map((bb) => (
              <div key={bb} className="flex items-center">
                <div className="flex items-center gap-2 text-xs text-text-primary">
                  {boardIcons[bb]}
                  <BoardBasisName boardBasis={bb} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {rooms &&
        hotelBookingData.rooms.map((room, index) => (
          <HotelRoom
            key={room.roomToken}
            room={room}
            adultCount={rooms[index].adults}
            childrenCount={rooms[index].children}
          />
        ))}
    </div>
  );
};
